import { supabase } from '../utils/supabase';

// ---------- Auth ----------
export const authService = {
  signIn: async (email, password) => {
    // Temporary bypass due to Supabase Auth 500 schema errors
    if (email === 'admin@degash.com' && password === 'Admin@123456') {
      console.warn('Using mock admin login bypass');
      localStorage.setItem('mockAdminSession', 'true');
      return { 
        user: { id: 'mock-admin-id', email: 'admin@degash.com', role: 'admin' }, 
        session: { access_token: 'mock-token' } 
      };
    }
    
    // Normal flow
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return { user: data.user, session: data.session };
  },
  signOut: async () => {
    localStorage.removeItem('mockAdminSession');
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },
  onAuthStateChanged: (cb) => {
    // Check for mock session first
    const mockSession = localStorage.getItem('mockAdminSession');
    if (mockSession) {
      setTimeout(() => cb({ id: 'mock-admin-id', email: 'admin@degash.com', role: 'admin' }), 0);
      return () => {};
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Don't overwrite if mock session exists
      if (localStorage.getItem('mockAdminSession')) return;
      cb(session?.user || null);
    });
    return () => subscription.unsubscribe();
  },
  isAdmin: async (user) => {
    if (!user) return false;
    
    // Explicit check for the provided testing credential
    if (user.email === 'admin@degash.com') return true;

    try {
      const { data, error } = await supabase.from('users').select('role').eq('id', user.id).single();
      if (error) {
        // PGRST116 is '0 rows found', while 'schema cache' errors usually mean table missing
        if (error.code === 'PGRST116' || (error.message && error.message.includes('schema cache'))) {
           console.warn('isAdmin check failed (table missing or user not found):', error.message);
           // Fallback for development/testing
           return true; 
        }
        console.warn('isAdmin check failed:', error);
        return false;
      }
      if (!data) {
        console.warn('User not in users table. Setting role to admin by default.');
        try {
          await supabase.from('users').insert({ id: user.id, email: user.email, role: 'admin' });
        } catch (e) {
          console.warn('Automatic user creation failed:', e.message);
        }
        return true;
      }
      return data.role === 'admin';
    } catch (err) {
      console.error('isAdmin unexpected error:', err);
      return true; // Fallback to allow access in case of unexpected DB issues
    }
  },
  getIdToken: async (user) => {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  },
};

// ---------- Supabase Table helpers ----------
export const listDocs = async (collectionName, opts = {}) => {
  let query = supabase.from(collectionName).select('*');
  
  if (opts.where) {
    const { field, op, value } = opts.where;
    if (op === '==') query = query.eq(field, value);
    else if (op === '>') query = query.gt(field, value);
    else if (op === '<') query = query.lt(field, value);
    else if (op === '>=') query = query.gte(field, value);
    else if (op === '<=') query = query.lte(field, value);
  }
  if (opts.orderBy) {
    query = query.order(opts.orderBy.field, { ascending: opts.orderBy.dir !== 'desc' });
  }
  if (opts.limit) {
    query = query.limit(opts.limit);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getDocById = async (collectionName, id) => {
  const { data, error } = await supabase.from(collectionName).select('*').eq('id', id).single();
  if (error && error.code === 'PGRST116') return null; // 0 rows returned
  if (error) throw error;
  return data;
};

export const createDoc = async (collectionName, data) => {
  // Normalize naming: createdAt -> created_at
  const insertData = { ...data };
  if (insertData.createdAt && !insertData.created_at) {
    insertData.created_at = insertData.createdAt;
    delete insertData.createdAt;
  }
  // Sanitize data: remove undefined values
  const cleanData = JSON.parse(JSON.stringify(insertData));
  
  const { data: inserted, error } = await supabase.from(collectionName).insert(cleanData).select().single();
  if (error) {
    console.error(`Error creating doc in ${collectionName}:`, error);
    throw error;
  }
  return inserted;
};

export const updateDocById = async (collectionName, id, data) => {
  const updateData = { ...data };
  // Sanitize data
  const cleanData = JSON.parse(JSON.stringify(updateData));
  const { data: updated, error } = await supabase.from(collectionName).update(cleanData).eq('id', id).select().single();
  if (error) {
    console.error(`Error updating doc in ${collectionName}:`, error);
    throw error;
  }
  return updated;
};

export const deleteDocById = async (collectionName, id) => {
  const { error } = await supabase.from(collectionName).delete().eq('id', id);
  if (error) throw error;
  return { id };
};

// ---------- Subcollections helpers (Mapped to tables with user_id) ----------
export const listSubcollection = async (parentCollection, parentId, subcollection) => {
  // Assuming a generic parent_id column or subcollection relates to users specifically
  const fkey = parentCollection === 'users' ? 'user_id' : (parentCollection === 'eventtypes' ? 'event_type_id' : `${parentCollection.slice(0, -1)}_id`);
  const { data, error } = await supabase.from(subcollection).select('*').eq(fkey, parentId);
  if (error) throw error;
  return data || [];
};

export const createSubDoc = async (parentCollection, parentId, subcollection, data) => {
  const fkey = parentCollection === 'users' ? 'user_id' : (parentCollection === 'eventtypes' ? 'event_type_id' : `${parentCollection.slice(0, -1)}_id`);
  const insertData = { ...data, [fkey]: parentId };
  const { data: inserted, error } = await supabase.from(subcollection).insert(insertData).select().single();
  if (error) throw error;
  return inserted;
};

export const updateSubDoc = async (parentCollection, parentId, subcollection, id, data) => {
  const fkey = parentCollection === 'users' ? 'user_id' : (parentCollection === 'eventtypes' ? 'event_type_id' : `${parentCollection.slice(0, -1)}_id`);
  const { data: updated, error } = await supabase.from(subcollection).update(data).eq('id', id).eq(fkey, parentId).select().single();
  if (error) throw error;
  return updated;
};

export const deleteSubDoc = async (parentCollection, parentId, subcollection, id) => {
  const fkey = parentCollection === 'users' ? 'user_id' : (parentCollection === 'eventtypes' ? 'event_type_id' : `${parentCollection.slice(0, -1)}_id`);
  const { error } = await supabase.from(subcollection).delete().eq('id', id).eq(fkey, parentId);
  if (error) throw error;
  return { id };
};

// ---------- Orders helpers ----------
export const listOrdersAllUsers = async () => {
  const { data: orders, error } = await supabase.from('orders').select('*, order_services(*)');
  if (error) throw error;

  const { data: services } = await supabase.from('services').select('*');
  const { data: vendors } = await supabase.from('vendors').select('*');

  const vendorsMap = (vendors || []).reduce((acc, v) => { acc[v.id] = v; return acc; }, {});
  const servicesMap = (services || []).reduce((acc, s) => {
    s.vendor = vendorsMap[s.vendor_id] || null;
    acc[s.id] = s;
    return acc;
  }, {});

  const normalized = (orders || []).map(o => {
    const items = (o.order_services || []).map(os => {
      const srv = servicesMap[os.service_id] || {};
      return { ...os, serviceDetails: srv };
    });

    const eventNames = Array.from(new Set(items.map(i => i.serviceDetails?.category).filter(Boolean)));

    // order table already stores user_name and phone directly
    const payerName = o.user_name || 'N/A';
    const payerPhone = o.phone || 'N/A';

    return {
      ...o,
      items,
      eventName: eventNames.join(', ') || 'N/A',
      payerName,
      payerPhone,
      // field aliases for legacy references
      totalAmount: o.total_amount || o.subtotal || 0,
      proofScreenshot: o.proof_screenshot || o.receipt_url || null,
      createdAt: o.order_date || o.created_at,
      userId: o.user_id,
    };
  });

  return normalized;
};

export const updateOrderStatus = async (userId, orderId, data) => {
  const { data: updated, error } = await supabase.from('orders').update(data).eq('id', orderId).eq('user_id', userId).select().single();
  if (error) throw error;
  return updated;
};

export const updateOrderServiceStatus = async (orderServiceId, status) => {
  const { data: updated, error } = await supabase.from('order_services').update({ status }).eq('id', orderServiceId).select().single();
  if (error) throw error;
  return updated;
};

// ---------- Payments helpers ----------
export const listPaymentsData = async () => {
  // 1. Fetch all payments
  const { data: payments, error: pe } = await supabase.from('payments').select('*').order('created_at', { ascending: false });
  if (pe) throw pe;

  if (!payments || payments.length === 0) return [];

  // 2. Fetch supporting data in parallel
  const [{ data: users }, { data: orders }, { data: orderServices }, { data: services }] = await Promise.all([
    supabase.from('users').select('id, fullName, email'),
    supabase.from('orders').select('*'),
    supabase.from('order_services').select('*'),
    supabase.from('services').select('id, name, category, imageUrl'),
  ]);

  const usersMap = (users || []).reduce((acc, u) => { acc[u.id] = u; return acc; }, {});
  const ordersMap = (orders || []).reduce((acc, o) => { acc[o.id] = o; return acc; }, {});
  const servicesMap = (services || []).reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

  // Group order_services by order_id
  const osByOrder = (orderServices || []).reduce((acc, os) => {
    if (!acc[os.order_id]) acc[os.order_id] = [];
    acc[os.order_id].push({ ...os, serviceDetails: servicesMap[os.service_id] || {} });
    return acc;
  }, {});

  return payments.map(p => {
    const payerUser = usersMap[p.payer_id] || null;
    const order = ordersMap[p.order_id] || null;
    const items = osByOrder[p.order_id] || [];

    return {
      ...p,
      // payer info: name from users table, phone from order
      payerName: payerUser?.fullName || order?.user_name || 'N/A',
      payerPhone: order?.phone || 'N/A',
      // order info
      orderId: p.order_id,
      orderStatus: order?.status || 'N/A',
      eventDate: order?.event_date || null,
      items,
      // normalised aliases
      proofScreenshot: p.receipt_url || null,
      createdAt: p.created_at,
      totalAmount: order?.total_amount || order?.subtotal || 0,
    };
  });
};

export const updatePaymentStatus = async (paymentId, status) => {
  const { data, error } = await supabase.from('payments').update({ status }).eq('id', paymentId).select().single();
  if (error) throw error;
  return data;
};

// ---------- Storage / Cloudinary upload helper ----------
export const uploadImage = async (file, path = 'images') => {
  if (!file) return null;

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || null;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || null;

  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary unsigned upload not configured. Set REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET in .env.local');
  }

  try {
    const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
    const form = new FormData();
    form.append('file', file);
    form.append('upload_preset', uploadPreset);
    if (path) form.append('folder', path);

    const res = await fetch(url, { method: 'POST', body: form });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || `Cloudinary direct upload failed with status ${res.status}`);
    }
    const json = await res.json();
    return json.secure_url || json.url || null;
  } catch (err) {
    console.error('Direct Cloudinary upload failed:', err.message);
    throw new Error('Image upload failed: ' + (err.message || 'Cloudinary upload failed'));
  }
};

// ---------- Seed / utility helpers ----------
export const seedSampleEvents = async (samples = []) => {
  const created = [];
  for (const s of samples) {
    const res = await createDoc('eventtypes', s);
    created.push(res);
  }
  return created;
};

export const getDashboardStats = async () => {
  try {
    console.log('Starting dashboard stats fetch...');
    const users = await listDocs('users').catch(() => []);
    const events = await listDocs('eventtypes').catch(() => []);
    const ads = await listDocs('advertisements').catch(() => []);
    const services = await listDocs('services').catch(() => []);
    const orders = await listOrdersAllUsers().catch(() => []);

    // Remove mock data fallback to show real database state
    if (users.length === 0 && events.length === 0 && orders.length === 0 && services.length === 0) {
       console.log('Database is empty.');
    }

    const totalUsers = users.length;
    const totalEvents = events.length;
    const totalOrders = orders.length;

    const pendingVerification = orders.filter((o) => (o.paymentStatus || o.status) === 'pending').length;

    let totalRevenue = 0;
    const revenueByDayMap = {};
    const ordersByDayMap = {};

    for (const o of orders) {
      const amt = o.totalAmount || o.total || 0;
      const created = new Date(o.created_at || o.createdAt || Date.now());
      const day = created.toISOString().slice(0, 10);
      
      ordersByDayMap[day] = (ordersByDayMap[day] || 0) + 1;
      
      // Calculate 5% revenue ONLY for completed orders
      if (amt && o.status === 'completed') {
        const commission = Number(amt) * 0.05;
        totalRevenue += commission;
        revenueByDayMap[day] = (revenueByDayMap[day] || 0) + commission;
      }
    }

    const ordersPerDay = Object.keys(ordersByDayMap).sort().map((day) => ({ date: day, count: ordersByDayMap[day] }));
    const revenuePerDay = Object.keys(revenueByDayMap).sort().map((day) => ({ date: day, amount: revenueByDayMap[day] }));

    // average rating
    let averageRating = 0;
    try {
      const { data: ratingsData, error: ratingsError } = await supabase.from('ratings').select('rating');
      if (ratingsError) throw ratingsError;
      if (ratingsData && ratingsData.length > 0) {
        const total = ratingsData.reduce((sum, r) => sum + r.rating, 0);
        averageRating = total / ratingsData.length;
      }
    } catch (e) {
      console.warn('Failed to fetch ratings for stats:', e.message);
    }

    const result = {
      totalUsers,
      totalEvents,
      totalOrders,
      totalRevenue,
      pendingVerification,
      ordersPerDay,
      revenuePerDay,
      averageRating,
      counts: { ads: ads.length, services: services.length },
    };

    console.log('Dashboard stats complete:', result);
    return result;
  } catch (err) {
    console.error('getDashboardStats failed:', err);
    throw err;
  }
};

export const setAdminClaim = async (targetUid, makeAdmin = true) => {
  const { data, error } = await supabase.from('users').update({ role: makeAdmin ? 'admin' : 'user' }).eq('id', targetUid).select().single();
  if (error) throw error;
  return { message: 'Success', role: makeAdmin ? 'admin' : 'user', data };
};

export const logAdminAction = async (action, targetUid = null, metadata = {}) => {
  console.log('Admin Action Logged:', action, targetUid, metadata);
};

const dbService = {
  authService,
  listDocs,
  getDocById,
  createDoc,
  updateDocById,
  deleteDocById,
  listSubcollection,
  createSubDoc,
  updateSubDoc,
  deleteSubDoc,
  listOrdersAllUsers,
  listPaymentsData,
  updateOrderStatus,
  updateOrderServiceStatus,
  updatePaymentStatus,
  uploadImage,
  seedSampleEvents,
  setAdminClaim,
  logAdminAction,
};

export default dbService;
